import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useT } from '../i18n.jsx';
import '../theme-palette.css';
import { User, Calendar, RefreshCw, UserCheck } from 'lucide-react';
import { useOutletContext } from 'react-router-dom';
import HeaderActionsPortal from './HeaderActionsPortal';

const DEFAULT_DAYS = [
  { code: 'M', name: 'Monday' },
  { code: 'T', name: 'Tuesday' },
  { code: 'W', name: 'Wednesday' },
  { code: 'H', name: 'Thursday' },
  { code: 'F', name: 'Friday' }
];

// Read-only viewer for the Teacher Timetable module's data -- any staff member can look up their own
// schedule (defaulted) or browse anyone else's, for any school year, but never edit or import/export it.
const MyTimetable = () => {
  const { activeSchoolId, userProfile, isMobile } = useOutletContext();
  const { t } = useT();
  const ownInitial = userProfile?.initial || '';

  const [loading, setLoading] = useState(true);
  const [loadingLessons, setLoadingLessons] = useState(false);

  const [teachers, setTeachers] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);

  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [selectedYearId, setSelectedYearId] = useState('');
  const [lessons, setLessons] = useState({});

  const days = DEFAULT_DAYS;

  useEffect(() => {
    if (!activeSchoolId) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const sSnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'staff_profiles')));
        const tList = [];
        sSnap.forEach(d => {
          const prof = d.data();
          const role = (prof.role || prof.Role || '').toLowerCase();
          if (role === 'teacher' || role === 'tch' || role === 'admin') {
            const initial = prof.initial || d.id.split('@')[0].toUpperCase();
            const teacherName = prof.displayName || (prof.lastName ? `${prof.lastName || ''} ${prof.firstName || ''}`.trim() : d.id.split('@')[0]);
            tList.push({ uid: d.id, name: `${initial} - ${teacherName}`, initial });
          }
        });
        tList.sort((a, b) => a.initial.localeCompare(b.initial));
        setTeachers(tList);

        const pSnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'timetable_profiles')));
        const pList = [];
        pSnap.forEach(d => pList.push({ uid: d.id, ...d.data() }));
        setProfiles(pList);
        if (pList.length > 0) {
          const defProfile = pList.find(p => p.isDefault) || pList[0];
          setSelectedProfileId(defProfile.uid);
        }

        const ySnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'academic_years')));
        const yList = [];
        ySnap.forEach(d => yList.push({ uid: d.id, ...d.data() }));
        yList.sort((a, b) => (a.yearId || '').localeCompare(b.yearId || ''));
        setAcademicYears(yList);
        if (yList.length > 0) {
          const currentYear = yList.find(y => y.cur_year) || yList[yList.length - 1];
          setSelectedYearId(currentYear.yearId);
        }
      } catch (e) {
        console.error('Error loading timetable reference data', e);
      }
      setLoading(false);
    };
    fetchData();
  }, [activeSchoolId]);

  // Default the viewer to their own schedule once we know their initial; only runs once so switching
  // to view someone else's timetable afterwards is never overridden.
  useEffect(() => {
    if (ownInitial) setSelectedTeacher(prev => prev || ownInitial);
  }, [ownInitial]);

  useEffect(() => {
    if (activeSchoolId && selectedTeacher && selectedYearId) {
      setLoadingLessons(true);
      const unsub = onSnapshot(collection(db, 'schools', activeSchoolId, 'teacher_lessons'), (snap) => {
        const teacherLessons = {};
        snap.forEach(d => {
          const data = d.data();
          if (data.T === selectedTeacher && data.Y === selectedYearId) {
            teacherLessons[data.P] = { C: data.C || '', S: data.S || '', R: data.R || '' };
          }
        });
        setLessons(teacherLessons);
        setLoadingLessons(false);
      });
      return () => unsub();
    } else {
      setLessons({});
    }
  }, [activeSchoolId, selectedTeacher, selectedYearId]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <RefreshCw className="animate-spin" size={24} color="var(--color-text-muted)" />
      </div>
    );
  }

  const currentProfile = profiles.find(p => p.uid === selectedProfileId);
  const periods = currentProfile?.periods || [];
  const isOwnSchedule = !!ownInitial && selectedTeacher === ownInitial;

  return (
    <div className="animate-fade-in new-theme" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>

      {/* Top Bar */}
      <div className="theme-tabs-bar">
        <div className="theme-tabs-group">
          <button className="theme-tab active">
            <div className="theme-tab-icon"><Calendar size={20} /></div>
            {t('myTimetable.title')}
          </button>
        </div>

        <HeaderActionsPortal>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            className="input-field"
            value={selectedYearId}
            onChange={e => setSelectedYearId(e.target.value)}
            style={{ width: '130px', margin: 0, padding: '0.4rem 0.5rem', height: 'auto', fontSize: '0.875rem' }}
          >
            {academicYears.length === 0 && <option value="">{t('myTimetable.noYear')}</option>}
            {academicYears.map(y => (
              <option key={y.uid} value={y.yearId}>{y.yearId}</option>
            ))}
          </select>

          <select
            className="input-field"
            value={selectedTeacher}
            onChange={e => setSelectedTeacher(e.target.value)}
            style={{ width: '200px', margin: 0, padding: '0.4rem 0.5rem', height: 'auto', fontSize: '0.875rem' }}
          >
            <option value="">{t('myTimetable.chooseTeacher')}</option>
            {teachers.map(teacher => (
              <option key={teacher.uid} value={teacher.initial}>{teacher.name}{teacher.initial === ownInitial ? ' (You)' : ''}</option>
            ))}
          </select>

          <select
            className="input-field"
            value={selectedProfileId}
            onChange={e => setSelectedProfileId(e.target.value)}
            style={{ width: '180px', margin: 0, padding: '0.4rem 0.5rem', height: 'auto', fontSize: '0.875rem' }}
          >
            {profiles.map(p => (
              <option key={p.uid} value={p.uid}>{p.name} {p.isDefault ? '(Default)' : ''}</option>
            ))}
          </select>

          {!isOwnSchedule && ownInitial && (
            <button
              onClick={() => setSelectedTeacher(ownInitial)}
              className="btn-secondary"
              style={{ width: 'auto', padding: '0.4rem 0.75rem', fontSize: '0.875rem', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
            >
              <UserCheck size={16} /> {t('myTimetable.title')}
            </button>
          )}
        </div>
        </HeaderActionsPortal>
      </div>

      {selectedTeacher ? (
        <div style={{ flex: 1, backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', overflow: 'auto', border: isMobile ? 'none' : '1px solid var(--color-border)', position: 'relative' }}>
          {loadingLessons && (
            <div style={{ position: 'absolute', top: '0.75rem', right: '0.75rem', zIndex: 20 }}>
              <RefreshCw className="animate-spin" size={16} color="var(--color-text-muted)" />
            </div>
          )}
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1000px' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--color-bg-app)' }}>
                <th style={{ padding: '0.75rem', borderBottom: '2px solid var(--color-border)', borderRight: '1px solid var(--color-border)', width: '150px', position: 'sticky', left: 0, backgroundColor: 'var(--color-bg-app)', zIndex: 10 }}>{t('myTimetable.period')}</th>
                {days.map(d => (
                  <th key={d.code} style={{ padding: '0.75rem', borderBottom: '2px solid var(--color-border)', borderRight: '1px solid var(--color-border)', textAlign: 'center', fontWeight: '600', color: 'var(--color-text-main)' }}>
                    {d.code}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {periods.map((period, pIndex) => (
                <tr key={pIndex}>
                  <td style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--color-border)', borderRight: '1px solid var(--color-border)', backgroundColor: (period.type === 'Recess' || period.type === 'Lunch') ? 'var(--color-success-bg)' : 'var(--color-bg-subtle)', position: 'sticky', left: 0, zIndex: 5 }}>
                    <div style={{ fontWeight: '600', color: 'var(--color-text-main)' }}>{period.name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{period.startTime} - {period.endTime}</div>
                  </td>
                  {days.map(d => {
                    let periodSlot;
                    if (period.type === 'Lesson') {
                      periodSlot = period.name.replace(/\D/g, '') || pIndex;
                    } else {
                      periodSlot = pIndex === 0 ? '0' : `${period.type.charAt(0).toUpperCase()}${period.name.replace(/\D/g, '') || pIndex}`;
                    }
                    const pCode = `${d.code}${periodSlot}`;
                    const lesson = lessons[pCode] || { C: '', S: '', R: '' };

                    return (
                      <td key={d.code} style={{ padding: '0.25rem', borderBottom: '1px solid var(--color-border)', borderRight: '1px solid var(--color-border)', verticalAlign: 'top', position: 'relative', backgroundColor: (period.type === 'Recess' || period.type === 'Lunch') ? 'var(--color-success-bg-soft)' : 'transparent' }}>
                        {period.type !== 'Lesson' && (
                          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'var(--color-text-muted)', fontSize: '0.75rem', fontStyle: 'italic', opacity: 0.15, pointerEvents: 'none', zIndex: 1 }}>
                            {t(`myTimetable.${String(period.type).toLowerCase()}`, {}, period.type)}
                          </div>
                        )}
                        {(lesson.C || lesson.S || lesson.R) && (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.15rem', padding: '0.15rem', position: 'relative', zIndex: 2 }}>
                            <div style={{ fontSize: '0.85rem', fontWeight: '600', color: 'var(--color-primary)' }}>{lesson.C || '-'}</div>
                            <div style={{ fontSize: '0.8rem', color: 'var(--color-text-main)' }}>{lesson.S || '-'}</div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{lesson.R || '-'}</div>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-card)', borderRadius: 'var(--radius-lg)', border: '1px dashed var(--color-border)' }}>
          <User size={48} style={{ opacity: 0.2, marginBottom: '1rem' }} />
          <p>{t('myTimetable.selectTeacher')}</p>
        </div>
      )}
    </div>
  );
};

export default MyTimetable;
